// Core
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

// Styles
import './styles.scss';

import {
  AppstoreOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  AudioMutedOutlined,
  PlaySquareOutlined,
  ReloadOutlined,
  SoundOutlined,
  TrophyOutlined,
  UserOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import { Fade } from 'react-reveal';

import { buildRound, OPTION_MARK, rankFor } from 'utils/quiz/engine';
import { LIBRARY } from 'utils/quiz/catalog';
import {
  bestVoice,
  listVoices,
  preloadArabicClips,
  preloadVoices,
  speak,
  speechSupported,
  stopSpeech,
} from 'utils/quiz/speech';

const STORAGE_KEY = 'cinemapedia-quiz-v1';
const QUESTION_SECONDS = 22;

const COPY = {
  title: { ar: 'كلاكيت', en: 'Claquette' },
  kicker: { ar: 'مسابقة السينما', en: 'CINEMA QUIZ' },
  tagline: {
    ar: 'وجوه من مصر والعالم، وأفلام من هوليوود وآسيا وأوروبا والشاشة العربية. أربع إجابات، وصوت يقرأ السؤال.',
    en: 'Faces from Egypt and the world, films from Hollywood, Asia, Europe, and the Arab screen. Four choices, with a voice that reads the question.',
  },
  start: { ar: 'ابدأ العرض', en: 'Start the show' },
  actors: { ar: 'ممثلون', en: 'Actors' },
  movies: { ar: 'أفلام', en: 'Films' },
  directors: { ar: 'مخرجون', en: 'Directors' },
  mix: { ar: 'مزيج', en: 'Mix' },
  actorsHint: { ar: 'من هذه الصورة؟', en: 'Name the face' },
  moviesHint: { ar: 'عنوان الفيلم', en: 'Name the film' },
  directorsHint: { ar: 'صاحب الكاميرا', en: 'Name the director' },
  mixHint: { ar: 'أسئلة منوعة', en: 'All categories' },
  questions: { ar: 'عدد الأسئلة', en: 'Questions' },
  best: { ar: 'أفضل نتيجة', en: 'Best' },
  score: { ar: 'النقاط', en: 'Score' },
  next: { ar: 'التالي', en: 'Next' },
  home: { ar: 'عودة', en: 'Back' },
  again: { ar: 'عرض آخر', en: 'Play again' },
  correct: { ar: 'إجابة صحيحة', en: 'Correct' },
  wrong: { ar: 'الإجابة الصحيحة', en: 'The answer is' },
  timeout: { ar: 'انتهى الوقت', en: 'Time is up' },
  result: { ar: 'نهاية العرض', en: 'End credits' },
  review: { ar: 'مراجعة الإجابات', en: 'Review' },
  voiceOn: { ar: 'الصوت يعمل', en: 'Voice on' },
  voiceOff: { ar: 'الصوت مغلق', en: 'Voice off' },
  speak: { ar: 'اقرأ السؤال', en: 'Read the question' },
  progress: { ar: 'سؤال', en: 'Question' },
  narrator: { ar: 'صوت الراوي', en: 'Narrator' },
  previewVoice: { ar: 'تجربة الصوت', en: 'Preview' },
  arabicVoiceNote: {
    ar: 'نستخدم صوتاً عربياً واضحاً (آرا) لقراءة الأسئلة والإجابات.',
    en: 'A clear Arabic voice (Ara) reads the questions and answers.',
  },
  library: {
    ar: `${LIBRARY.actors} نجماً · ${LIBRARY.directors} مخرجاً · ${LIBRARY.movies} سؤالاً`,
    en: `${LIBRARY.actors} stars · ${LIBRARY.directors} directors · ${LIBRARY.movies} questions`,
  },
};

const CATEGORIES = [
  { id: 'actors', icon: UserOutlined },
  { id: 'movies', icon: PlaySquareOutlined },
  { id: 'directors', icon: VideoCameraOutlined },
  { id: 'mix', icon: AppstoreOutlined },
];

const HINTS = {
  actors: 'actorsHint',
  movies: 'moviesHint',
  directors: 'directorsHint',
  mix: 'mixHint',
};

const ORDER_AR = ['الأولى:', 'الثانية:', 'الثالثة:', 'الرابعة:'];

const loadSaved = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { voice: true, best: {}, voiceUri: {} };
    const parsed = JSON.parse(raw);
    return {
      voice: parsed.voice !== false,
      best: parsed.best || {},
      voiceUri: parsed.voiceUri || {},
    };
  } catch (e) {
    return { voice: true, best: {}, voiceUri: {} };
  }
};

const questionParts = (q) => {
  const parts = [q.prompt.ar, 'الإجابات.'];
  q.shuffled.forEach((opt, i) => {
    parts.push(ORDER_AR[i], opt.ar);
  });
  return parts;
};

const questionSpeech = (q, lang) => {
  if (lang === 'ar') return q.prompt.ar;
  const marks = OPTION_MARK.en;
  const options = q.shuffled.map((opt, i) => `Option ${marks[i]}: ${opt.en}`).join('. ');
  return `${q.prompt.en}. ${options}`;
};

const Quiz = () => {
  const { currentLang } = useSelector((store) => store.commonData);
  const lang = currentLang?.lang === 'en' ? 'en' : 'ar';
  const dir = lang === 'ar' ? 'rtl' : 'ltr';
  const t = useCallback((key) => COPY[key][lang], [lang]);

  const [phase, setPhase] = useState('home');
  const [voice, setVoice] = useState(true);
  const [voiceUri, setVoiceUri] = useState({});
  const [voiceList, setVoiceList] = useState([]);
  const [best, setBest] = useState({});
  const [category, setCategory] = useState('mix');
  const [count, setCount] = useState(10);
  const [round, setRound] = useState([]);
  const [index, setIndex] = useState(0);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);
  const [seconds, setSeconds] = useState(QUESTION_SECONDS);
  const [timedOut, setTimedOut] = useState(false);
  const [history, setHistory] = useState([]);
  const [hydrated, setHydrated] = useState(false);

  const question = round[index];
  const answered = picked !== null || timedOut;
  const marks = OPTION_MARK[lang];
  const bestFor = best[category] || 0;

  const say = useCallback(
    (text, parts) => {
      speak(text, lang, { voiceUri: voiceUri[lang], parts });
    },
    [lang, voiceUri]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    const saved = loadSaved();
    setVoice(saved.voice);
    setBest(saved.best);
    setVoiceUri(saved.voiceUri);
    setHydrated(true);
    preloadArabicClips();
    return () => stopSpeech();
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ voice, best, voiceUri }));
    } catch (e) {
      /* storage unavailable */
    }
  }, [hydrated, voice, best, voiceUri]);

  useEffect(() => {
    const off = preloadVoices(() => setVoiceList(listVoices(lang)));
    setVoiceList(listVoices(lang));
    if (lang === 'en') {
      const preferred = bestVoice('en');
      if (preferred) {
        setVoiceUri((prev) => (prev.en ? prev : { ...prev, en: preferred.uri }));
      }
    }
    return off;
  }, [lang]);

  // The narration language follows the site language, so stop any clip mid-flight.
  useEffect(() => {
    stopSpeech();
  }, [lang]);

  useEffect(() => {
    if (phase !== 'play' || answered) return undefined;
    const id = window.setInterval(() => {
      setSeconds((s) => {
        if (s <= 1) {
          setTimedOut(true);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, [phase, index, answered]);

  const start = (nextCategory) => {
    const useCategory = nextCategory || category;
    const built = buildRound(useCategory, count);
    if (!built.length) return;
    setCategory(useCategory);
    setRound(built);
    setIndex(0);
    setPicked(null);
    setTimedOut(false);
    setScore(0);
    setHistory([]);
    setPhase('play');
    setSeconds(QUESTION_SECONDS);
    if (voice) {
      say(questionSpeech(built[0], lang), lang === 'ar' ? questionParts(built[0]) : undefined);
    }
  };

  const choose = (key) => {
    if (answered || !question) return;
    setPicked(key);
    const ok = key === question.correctKey;
    if (ok) setScore((s) => s + 1);
    setHistory((h) => [...h, { id: question.id, correct: ok }]);
    if (!voice) return;
    if (ok) {
      say(lang === 'ar' ? 'إجابة صحيحة.' : 'Correct', ['إجابة صحيحة.']);
    } else {
      const line =
        lang === 'ar'
          ? `الإجابة الصحيحة هي ${question.answer.ar}`
          : `The correct answer is ${question.answer.en}`;
      say(line, ['الإجابة الصحيحة هي', question.answer.ar]);
    }
  };

  // Keep the latest choose() reachable from the key handler without re-binding each render.
  const chooseRef = useRef(choose);
  chooseRef.current = choose;

  useEffect(() => {
    if (phase !== 'play') return undefined;
    const KEY_TO_OPTION = { 1: 0, 2: 1, 3: 2, 4: 3, a: 0, b: 1, c: 2, d: 3 };
    const handler = (e) => {
      const i = KEY_TO_OPTION[String(e.key).toLowerCase()];
      const q = round[index];
      if (i == null || !q || !q.shuffled[i]) return;
      chooseRef.current(q.shuffled[i].en);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [phase, round, index]);

  const goNext = () => {
    if (!question) return;
    if (index + 1 >= round.length) {
      setBest((b) => {
        const prev = b[category] || 0;
        return score > prev ? { ...b, [category]: score } : b;
      });
      stopSpeech();
      setPhase('result');
      window.scrollTo(0, 0);
      if (voice) {
        const line =
          lang === 'ar'
            ? `نهاية العرض. حصلت على ${score} من ${round.length}`
            : `End credits. You scored ${score} out of ${round.length}`;
        say(line, ['نهاية العرض.', 'حصلت على', String(score), 'من', String(round.length)]);
      }
      return;
    }
    setIndex((i) => i + 1);
    setPicked(null);
    setTimedOut(false);
    setSeconds(QUESTION_SECONDS);
    const next = round[index + 1];
    if (voice && next) {
      say(questionSpeech(next, lang), lang === 'ar' ? questionParts(next) : undefined);
    }
  };

  useEffect(() => {
    if (phase !== 'play' || !timedOut || picked !== null || !question) return;
    setHistory((h) =>
      h.some((x) => x.id === question.id) ? h : [...h, { id: question.id, correct: false }]
    );
    if (!voice) return;
    const line =
      lang === 'ar'
        ? `انتهى الوقت. الإجابة الصحيحة هي ${question.answer.ar}`
        : `Time is up. The correct answer is ${question.answer.en}`;
    say(line, ['انتهى الوقت.', 'الإجابة الصحيحة هي', question.answer.ar]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timedOut, phase, picked, question]);

  const resultRank = useMemo(() => rankFor(score, round.length, lang), [score, round.length, lang]);

  const toggleVoice = () => {
    const next = !voice;
    setVoice(next);
    if (next) {
      speak(lang === 'ar' ? 'الصوت يعمل.' : 'Voice on', lang, {
        voiceUri: voiceUri[lang],
        parts: ['الصوت يعمل.'],
      });
    } else {
      stopSpeech();
    }
  };

  const previewVoice = () => {
    setVoice(true);
    if (lang === 'ar') {
      speak('مرحباً. أنا صوت المسابقة. من هذا الممثل؟', 'ar', {
        parts: ['مرحباً. أنا صوت المسابقة. من هذا الممثل؟'],
      });
    } else {
      speak('Hello. I am the quiz narrator. Who is this actor?', 'en', {
        voiceUri: voiceUri.en,
      });
    }
  };

  const BackArrow = dir === 'rtl' ? ArrowRightOutlined : ArrowLeftOutlined;

  return (
    <div className="__quiz" dir={dir}>
      {phase === 'home' && (
        <div className="__quiz_home">
          <img className="__quiz_home_bg" src="/quiz/theater.jpg" alt="" />
          <div className="__quiz_home_veil" />
          <div className="container __quiz_home_inner">
            <div className="__quiz_bar">
              <button
                type="button"
                className="__quiz_chip"
                onClick={toggleVoice}
                aria-pressed={voice}
              >
                {voice ? <SoundOutlined /> : <AudioMutedOutlined />}
                {voice ? t('voiceOn') : t('voiceOff')}
              </button>
            </div>

            {voice && (
              <div className="__quiz_voice_box">
                <div className="__quiz_voice_head">
                  <span>{t('narrator')}</span>
                  <button type="button" className="__quiz_link" onClick={previewVoice}>
                    {t('previewVoice')}
                  </button>
                </div>
                {lang === 'en' && voiceList.length > 1 ? (
                  <select
                    className="__quiz_select"
                    value={voiceUri.en || voiceList[0]?.uri || ''}
                    onChange={(e) => setVoiceUri((prev) => ({ ...prev, en: e.target.value }))}
                  >
                    {voiceList.map((item) => (
                      <option key={item.uri} value={item.uri}>
                        {item.recommended ? `${item.label} — recommended` : item.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="__quiz_note">{t('arabicVoiceNote')}</p>
                )}
              </div>
            )}

            <Fade bottom cascade>
              <div className="__quiz_hero">
                <img className="__quiz_clapper" src="/quiz/clapper.jpg" alt="" />
                <p className="__quiz_kicker">{t('kicker')}</p>
                <h1 className="__quiz_title">{t('title')}</h1>
                <p className="__quiz_tagline">{t('tagline')}</p>
                <p className="__quiz_library">{t('library')}</p>
              </div>
            </Fade>

            <div className="__quiz_counts">
              <span className="__quiz_counts_label">{t('questions')}</span>
              {[8, 10, 12].map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`__quiz_count ${count === n ? 'is-active' : ''}`}
                  onClick={() => setCount(n)}
                >
                  {n}
                </button>
              ))}
              {best[category] != null && (
                <span className="__quiz_best">
                  <TrophyOutlined /> {t('best')}: {best[category]}
                </span>
              )}
            </div>

            <div className="__quiz_cats">
              {CATEGORIES.map(({ id, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  className={`__quiz_cat ${category === id ? 'is-active' : ''}`}
                  onClick={() => {
                    setCategory(id);
                    start(id);
                  }}
                >
                  <Icon className="__quiz_cat_icon" />
                  <span className="__quiz_cat_name">{t(id)}</span>
                  <span className="__quiz_cat_hint">{t(HINTS[id])}</span>
                </button>
              ))}
            </div>

            <button type="button" className="__quiz_cta" onClick={() => start(category)}>
              {t('start')}
            </button>
          </div>
        </div>
      )}

      {phase === 'play' && question && (
        <div className="container __quiz_play __innerTopPad">
          <div className="__quiz_play_head">
            <button
              type="button"
              className="__quiz_link"
              onClick={() => {
                stopSpeech();
                setPhase('home');
              }}
            >
              <BackArrow /> {t('home')}
            </button>
            <span className="__quiz_meta">
              {t('progress')} {index + 1} / {round.length}
            </span>
            <span className="__quiz_meta">
              {t('score')} {score}
            </span>
            <button
              type="button"
              className="__quiz_icon_btn"
              aria-label={t('speak')}
              onClick={() => {
                if (!voice) setVoice(true);
                if (speechSupported()) {
                  say(
                    questionSpeech(question, lang),
                    lang === 'ar' ? questionParts(question) : undefined
                  );
                }
              }}
            >
              <SoundOutlined />
            </button>
          </div>

          <div className="__quiz_progress">
            <div
              className="__quiz_progress_fill"
              style={{ width: `${((index + (answered ? 1 : 0)) / round.length) * 100}%` }}
            />
          </div>

          <p className={`__quiz_timer ${seconds <= 5 ? 'is-low' : ''}`}>{seconds}s</p>

          <div className="row">
            <div className="col-md-5 mb-4">
              <div className={`__quiz_stage ${question.frame === 'wide' ? 'is-wide' : ''}`}>
                <img src={question.image} alt="" />
              </div>
            </div>
            <div className="col-md-7">
              <h2 className="__quiz_prompt">{question.prompt[lang]}</h2>
              <div className="__quiz_options">
                {question.shuffled.map((opt, i) => {
                  const isCorrect = opt.en === question.correctKey;
                  const isPick = picked === opt.en;
                  let state = '';
                  if (answered) {
                    if (isCorrect) state = 'is-correct';
                    else if (isPick) state = 'is-wrong';
                    else state = 'is-dim';
                  }
                  return (
                    <button
                      key={opt.en}
                      type="button"
                      className={`__quiz_option ${state}`}
                      disabled={answered}
                      onClick={() => choose(opt.en)}
                    >
                      <span className="__quiz_option_mark">{marks[i]}</span>
                      <span className="__quiz_option_text">{opt[lang]}</span>
                    </button>
                  );
                })}
              </div>

              {answered && (
                <div className="__quiz_reveal">
                  <p
                    className={`__quiz_verdict ${
                      picked === question.correctKey ? 'is-correct' : 'is-wrong'
                    }`}
                  >
                    {picked === question.correctKey
                      ? t('correct')
                      : `${timedOut ? `${t('timeout')} · ` : ''}${t('wrong')}: ${
                          question.answer[lang]
                        }`}
                  </p>
                  <p className="__quiz_fact">{question.fact[lang]}</p>
                  <button type="button" className="__quiz_cta" onClick={goNext}>
                    {index + 1 >= round.length ? t('result') : t('next')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {phase === 'result' && (
        <div className="container __quiz_result __innerTopPad">
          <Fade bottom cascade>
            <div>
              <p className="__quiz_kicker">{t('result')}</p>
              <h1 className="__quiz_rank">{resultRank}</h1>
              <p className="__quiz_score">
                {score} / {round.length}
              </p>
              {bestFor > 0 && (
                <p className="__quiz_best">
                  <TrophyOutlined /> {t('best')}: {Math.max(bestFor, score)}
                </p>
              )}
            </div>
          </Fade>

          <h2 className="__quiz_review_head">{t('review')}</h2>
          <ul className="__quiz_review">
            {round.map((q, i) => {
              const ok = history.find((h) => h.id === q.id)?.correct;
              return (
                <li key={q.id} className="__quiz_review_row">
                  <img src={q.image} alt="" />
                  <div className="__quiz_review_body">
                    <p className="__quiz_review_answer">{q.answer[lang]}</p>
                    <p className="__quiz_review_prompt">{q.prompt[lang]}</p>
                  </div>
                  <span className={`__quiz_review_flag ${ok ? 'is-correct' : 'is-wrong'}`}>
                    {ok ? (lang === 'ar' ? 'صح' : 'Yes') : lang === 'ar' ? 'خطأ' : 'No'}
                  </span>
                  <span className="__quiz_review_no">{i + 1}</span>
                </li>
              );
            })}
          </ul>

          <div className="__quiz_result_actions">
            <button type="button" className="__quiz_cta" onClick={() => start(category)}>
              <ReloadOutlined /> {t('again')}
            </button>
            <button
              type="button"
              className="__quiz_cta is-ghost"
              onClick={() => setPhase('home')}
            >
              {t('home')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Quiz;
